import React from "react";
import {NonLifestyleGoalType} from "../models/GoalType";
import {DiscountRateType} from "../models/DiscountRate";
import {formatNumber} from "../../utils/format";
import {Button, PercentInput, UnderlinedHeader} from "../../components";
import {useAnalyticsTrackers} from "../../utils/useAnalyticsTrackers";
import {useAppSelector} from "../../store/hooks";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";

interface GoalGlidePathProps {
    nonLifestyleGoal: NonLifestyleGoalType,
    discountRateSelection: DiscountRateType,
    updateNonLifestyleGoal: (updateNonLifestyleGoalGlidePath: Partial<NonLifestyleGoalType["userInputs"]>) => void,
    updatePresentValue: () => void,
    resetRiskControlAsset: () => void,
    isProfileWithProposalsOrArchived?: boolean
}

const GoalGlidePath = ({
                                 nonLifestyleGoal,
                                 updateNonLifestyleGoal,
                                 updatePresentValue,
                                 resetRiskControlAsset,
                                 discountRateSelection,
                                 isProfileWithProposalsOrArchived = false
                              }: GoalGlidePathProps) => {
    const isRiskInfoInUserInput = nonLifestyleGoal.userInputs.riskAsset  !=  null

    const {page} = useAnalyticsTrackers();
    const enableEnhancedLifestyleFunding = useAppSelector(selectReleaseToggles)?.enableEnhancedLifestyleFunding ?? false;

    let riskAssetsValue = isRiskInfoInUserInput
        ? nonLifestyleGoal.userInputs.riskAsset
        : nonLifestyleGoal.calculatedFields.riskAsset;

    let riskControlValue = isRiskInfoInUserInput
        ? nonLifestyleGoal.userInputs.riskControl
        : nonLifestyleGoal.calculatedFields.riskControl;

    if(riskAssetsValue == 0) {
        riskControlValue = 100;
    } else if(riskControlValue == 0){
        riskAssetsValue = 100;
    }

    const riskAssetsDisplayValue = riskAssetsValue != null ? Math.round(riskAssetsValue).toString() : '0';
    const riskControlDisplayValue = riskControlValue != null ? Math.round(riskControlValue).toString() : '0';

    const handleChangeRiskAssets = (newValue: string) => {
        const riskAsset = Number(newValue);
        let newValueNumber = 100 - riskAsset;
        const riskControl = newValueNumber < 0 ? 0 : newValueNumber
        updateNonLifestyleGoal({riskAsset, riskControl});
    }

    const handleChangeRiskControlAssets = (newValue: string) => {
        const riskControl = Number(newValue)
        let newValueNumber = 100 - riskControl;
        const riskAsset = newValueNumber < 0 ? 0 : newValueNumber
        updateNonLifestyleGoal({riskAsset, riskControl});
    }


    return (
        <section aria-label="Proposed Glidepath Allocation">
            <UnderlinedHeader
                primaryText='Proposed Glidepath Allocation'
                rightAlignedContent={
                        <Button
                            icon="left"
                            iconName="restore"
                            includeRef={false}
                            kind="borderless"
                            onClick={resetRiskControlAsset}
                            size="small"
                            tabIndex={0}
                            type="button"
                            style={{
                                visibility: isRiskInfoInUserInput ? 'visible' : 'hidden'
                            }}
                        >
                            REVERT TO GLIDEPATH
                        </Button>
                }
                className="goal-glidepath-underlined-header"
            />
            <div className={`family-goal-glide-path-table${!enableEnhancedLifestyleFunding ? ' family-goal-glide-path-table--with-discount-rate' : ''}`}>
                <label>Risk Assets</label>
                <PercentInput
                    defaultValue=""
                    label="riskAssets"
                    hideLabel
                    value={riskAssetsDisplayValue}
                    minValue={0}
                    maxValue={100}
                    maxLength={2}
                    onChange={handleChangeRiskAssets}
                    onBlur={() => {
                        page(`${nonLifestyleGoal.goalType}_Glide_Path_Value_Click`);
                        updatePresentValue()
                    }}
                    formatOptions={{maximumFractionDigits: 0}}
                    disableOnChangeCallWhenHandlingFocusEvent={true}
                    disabled={isProfileWithProposalsOrArchived}
                />
                {!enableEnhancedLifestyleFunding && (
                    <DiscountRateDiv
                        discountRateSelection={discountRateSelection}
                        calculatedFields={nonLifestyleGoal.calculatedFields}
                    />
                )}
                <label>Risk Control Assets</label>
                <PercentInput
                    defaultValue=""
                    label="riskControlAssets"
                    hideLabel
                    value={riskControlDisplayValue}
                    minValue={0}
                    maxValue={100}
                    maxLength={2}
                    onChange={handleChangeRiskControlAssets}
                    onBlur={() => {
                        page(`${nonLifestyleGoal.goalType}_Glide_Path_Value_Click`);
                        updatePresentValue()
                    }}
                    formatOptions={{maximumFractionDigits: 0}}
                    disableOnChangeCallWhenHandlingFocusEvent={true}
                    disabled={isProfileWithProposalsOrArchived}
                />
            </div>
        </section>
    )
}

function DiscountRateDiv({calculatedFields, discountRateSelection}: {calculatedFields: NonLifestyleGoalType['calculatedFields'], discountRateSelection: DiscountRateType}) {
    return discountRateSelection === DiscountRateType.TRIPLE_NET
        ? (
            <div style={{marginTop: 10}}>
                <span className="marginleft-lg"><b>Triple Net Discount Rate</b></span>
                <span
                    className="marginleft-lg">{formatNumber(calculatedFields!.tripleNetDiscountRate)}%</span>
            </div>
        )
        : (
            <div style={{marginTop: 10}}>
                <span className="marginleft-lg"><b>Gross Discount Rate</b></span>
                <span
                    className="marginleft-lg">{formatNumber(calculatedFields!.grossDiscountRate)}%</span>
            </div>
        );
}
export default GoalGlidePath;
